import React, { useMemo } from 'react';
import isNil from 'lodash/isNil';
import { useAppSelector } from '../../../../../redux/hooks';
import { selectModalData } from '../../../../../redux/selectors/modal';
import SaveMeasureModal from './SaveMeasureModal';
import { ISaveMeasureState } from './types';
import usePermission from '../../../../../utils/hooks/permissionHooks/usePermission';
import {
  IMeasureFieldsFragment,
  PermissionKey,
} from '../../../../../utils/apollo/graphqlSchema';
import { useStrategyOptions } from '../../../../../utils/hooks';
import { ISaveMeasureInputs } from '../utils/types';
import { assertNotNil } from '../../../../../utils/types';

interface IProps {
  schemaId: string;
}

const SaveMeasureModalController = ({ schemaId }: IProps) => {
  const { checkIsPermitted } = usePermission();

  const isPermittedToCreateMeasure = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.MeasureCreate],
    schemaId,
  });
  const isPermittedToUpdateMeasure = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.MeasureEdit],
    schemaId,
  });

  const modalData: ISaveMeasureInputs = useAppSelector(selectModalData);

  assertNotNil(modalData);

  const defaultNamedMeasure: IMeasureFieldsFragment | null = useMemo(() => {
    if (modalData.type === 'simple' && modalData.isNamed) {
      return modalData.simpleMeasure;
    }

    return null;
  }, [modalData]);

  const defaultValues: Partial<ISaveMeasureState> = useMemo(() => {
    const result: Partial<ISaveMeasureState> = {
      strategy: 'create',
    };

    if (!isNil(modalData?.format)) {
      const { __typename, ...format } = modalData.format;
      result.format = format;
    }

    if (modalData.type === 'simple') {
      const { simpleMeasure, isNamed } = modalData;
      if (isNamed && isPermittedToUpdateMeasure) {
        assertNotNil(simpleMeasure.version?.versionId);

        result.strategy = 'update';
        result.versionId = simpleMeasure.version.versionId;
      }

      return result;
    }

    const defaultStrategy = isPermittedToCreateMeasure ? 'create' : 'update';

    return {
      ...result,
      strategy: defaultStrategy,
    };
  }, [isPermittedToCreateMeasure, isPermittedToUpdateMeasure, modalData]);

  const { strategyOptions } = useStrategyOptions({
    defaultStrategy: defaultValues?.strategy ?? 'create',
    permittedStatus: {
      create: isPermittedToCreateMeasure,
      update: isPermittedToUpdateMeasure,
    },
  });

  return (
    <SaveMeasureModal
      defaultNamedMeasure={defaultNamedMeasure}
      defaultValues={defaultValues}
      schemaId={schemaId}
      strategyOptions={strategyOptions}
      inputs={modalData}
    />
  );
};

export default SaveMeasureModalController;
